import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import isNil from 'lodash/isNil';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { OrganizationSelectors } from '@copilot/common/store/selectors/organization';
import { RootState } from '../../../store/reducers/index';
import { isEmpty } from 'lodash';

type PendoProps = Readonly<{
    apiKey: string;
}>;

declare global {
    interface Window {
        pendo: any;
    }
}

/**
 * Pendo component to inject the tracking code and handle user identification
 */
const Pendo = ({ apiKey }: PendoProps) => {
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    const activeOrganization = useSelector(OrganizationSelectors.getActiveOrganization);
    const activeImpersonator = useSelector((state: RootState) => state.app.impersonator);
    // Initialize Pendo script with stubs to queue calls until agent loads
    useEffect(() => {
        const canUseDOM = !!(
            typeof window !== 'undefined' &&
            window.document &&
            window.document.createElement
        );

        if (!apiKey || !canUseDOM || window.pendo) return;

        (function (pendoKey) {
            const pendo = (window.pendo = window.pendo || {});
            pendo._q = pendo._q || [];

            // Create stub methods that queue calls until the agent replaces them
            const methods = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
            methods.forEach((method) => {
                if (!pendo[method]) {
                    pendo[method] = function () {
                        const args = Array.prototype.slice.call(arguments, 0);
                        pendo._q[method === 'initialize' ? 'unshift' : 'push']([method, ...args]);
                    };
                }
            });

            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.src = 'https://cdn.pendo.io/agent/static/' + pendoKey + '/pendo.js';
            document.head.appendChild(script);
        })(apiKey);
    }, [apiKey]);

    // Identify user when active member or organization changes
    useEffect(() => {
        if (!window.pendo?.initialize || isNil(activeMember) || isNil(activeOrganization)) {
            return;
        }

        const userIdToTrack =
            isNil(activeImpersonator) || isEmpty(activeImpersonator)
                ? activeMember.userId
                : activeImpersonator;

        const visitorData = {
            id: userIdToTrack,
            email: activeMember.email,
            full_name: activeMember.name,
            firstName: activeMember.firstName,
            lastName: activeMember.lastName,
            role: activeMember.orgRoles?.[0] || '',
            createdAt: activeMember.userCreatedDate
                ? new Date(activeMember.userCreatedDate * 1000).toISOString()
                : undefined,
            impersonator: activeImpersonator ?? '',
        };

        const accountData = {
            id: activeOrganization.id,
            name: activeOrganization.name,
            maxSeats: activeOrganization.maxSeats,
            allocatedSeats: activeOrganization.seatsAllocated,
            isFreeTrial: activeOrganization.isFreeTrial,
            trialFlow: activeOrganization.trialFlowName,
            createdAt: activeOrganization.tenantCreatedAt,
            parentTenantId: activeOrganization.parentTenantId,
            parentTenantName: activeOrganization.parentTenantName,
            stripeCustomerId: activeOrganization.stripeCustomerId,
            stripeSubscriptionId: activeOrganization.stripeSubscriptionId,
        };

        if (!isNil(window.pendo.visitorId)) {
            window.pendo.identify({
                visitor: visitorData,
                account: accountData,
            });
        } else {
            window.pendo.initialize({
                visitor: visitorData,
                account: accountData,
            });
        }
    }, [activeMember?.userId, activeOrganization?.id, activeImpersonator]);

    return null;
};

export default Pendo;
